import { ApiRequestError, Textarea, cn } from '@h2hspark/ui';
import { Send } from 'lucide-react';
import * as React from 'react';

import { useSendMessage } from './api';
import {
  ACCOUNT_DELETED_NOTICE,
  COMPOSER_LABEL,
  COMPOSER_PLACEHOLDER,
  SEND_FAILED,
  SEND_LABEL,
  SEND_REFUSED_DISCONNECTED,
  SEND_REFUSED_RATE_LIMITED,
} from './constants';

/**
 * The composer — `Web App.dc.html` §2.16's pill textarea and 44px spark send button, and §2.11's
 * card variant with a labelled Send.
 *
 * ## The refusal is rendered, not thrown away
 *
 * `MEM-054` AC-5 keeps a thread readable after a disconnect, so a member can be looking at a
 * composer whose send the server will refuse — the pair disconnected while the screen was open,
 * which is the case the read-only thread exists for. The API answers each refusal with its own
 * reason (409 `disconnected`, 409 `account-deleted`, 429 `rate-limited`), and each is a different
 * sentence here.
 *
 * A toast would have been less work and is wrong for this: the message is still in the box, the
 * member is about to try again, and the explanation belongs next to the control they will press.
 *
 * ## Enter sends, Shift+Enter breaks the line
 *
 * The convention every messenger has, and the one a member will try first. It is worth naming
 * because the alternative — a textarea where Enter only ever inserts a newline — makes the Send
 * button the only way out, which is a reach on a phone with a keyboard covering half the screen.
 */
export function MessageComposer({
  connectionRef,
  variant,
  onSent,
}: {
  readonly connectionRef: string;
  /** `overlay` is §2.16's pill row; `card` is §2.11's bordered footer with a labelled button. */
  readonly variant: 'overlay' | 'card';
  readonly onSent?: () => void;
}): React.ReactElement {
  const send = useSendMessage(connectionRef);
  const [body, setBody] = React.useState('');
  const [refusal, setRefusal] = React.useState<string | null>(null);
  const empty = body.trim().length === 0;

  const submit = (): void => {
    if (empty || send.isPending) return;
    setRefusal(null);
    send.mutate(
      { body },
      {
        onSuccess: () => {
          setBody('');
          onSent?.();
        },
        onError: (error) => {
          setRefusal(refusalSentence(error));
        },
      },
    );
  };

  return (
    <div className={cn(variant === 'card' ? 'border-t border-border-subtle px-[22px] py-4' : '')}>
      {refusal === null ? null : (
        <p role="alert" className="mb-2 text-xs text-destructive">
          {refusal}
        </p>
      )}
      <form
        className="flex items-end gap-2.5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="sr-only" htmlFor={`composer-${connectionRef}`}>
          {COMPOSER_LABEL}
        </label>
        <Textarea
          id={`composer-${connectionRef}`}
          value={body}
          rows={1}
          placeholder={COMPOSER_PLACEHOLDER}
          className={cn(
            'min-h-[52px] flex-1 resize-y',
            variant === 'overlay' ? 'rounded-[22px] px-4 py-3' : 'rounded-md bg-card',
          )}
          onChange={(changed) => {
            setBody(changed.target.value);
            setRefusal(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <button
          type="submit"
          disabled={empty || send.isPending}
          aria-label={SEND_LABEL}
          className={cn(
            'inline-flex flex-none items-center justify-center gap-1.5 bg-spark font-brand text-sm font-bold text-white disabled:opacity-60',
            variant === 'overlay'
              ? 'h-11 w-11 rounded-pill'
              : 'rounded-pill px-5 py-3.5',
          )}
        >
          <Send className="h-4 w-4" aria-hidden />
          {variant === 'card' ? SEND_LABEL : null}
        </button>
      </form>
    </div>
  );
}

/**
 * The server's refusal, in the member's words.
 *
 * `not-a-connection` is deliberately absent: the thread would not have opened. It falls through
 * to {@link SEND_FAILED}, which is what a 404 on a screen that is already showing the
 * conversation actually means — something went wrong, try again — rather than a sentence
 * explaining a state the member cannot see.
 */
function refusalSentence(error: Error): string {
  if (!(error instanceof ApiRequestError)) return SEND_FAILED;
  if (error.statusCode === 429) return SEND_REFUSED_RATE_LIMITED;
  if (error.statusCode === 409) {
    // Both closures answer 409 — the relationship changed, which is what a conflict is — so the
    // reason is read from the envelope rather than from the status. `account-deleted` gets the
    // thread's own line, which is already above the composer, rather than a second sentence
    // saying the same thing twice on one screen.
    return error.toUserMessage() === 'account-deleted'
      ? ACCOUNT_DELETED_NOTICE
      : SEND_REFUSED_DISCONNECTED;
  }
  return SEND_FAILED;
}
